import { useEffect, useState, useCallback } from "react";
import { Plus, Loader2, Inbox, ArrowUpRight, ArrowDownRight, ArrowLeftRight, Pencil, Trash2, Eye } from "lucide-react";
import { TransactionDetails } from "@/components/TransactionDetails";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtAmount, fmtDate } from "@/lib/render";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const TYPE_META = {
  inflow: { icon: ArrowUpRight, cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  outflow: { icon: ArrowDownRight, cls: "bg-red-500/15 text-red-300 border-red-500/30" },
  transfer: { icon: ArrowLeftRight, cls: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30" },
};

const EMPTY = { type: "inflow", account_id: "", counter_account_id: "", amount: "", currency: "USD", description: "", reference: "" };

export default function TransactionsPage() {
  const { user } = useAuth();
  const canWrite = ["admin", "staff", "company"].includes(user.role);
  const [rows, setRows] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/transactions");
      setRows(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setRows([]); }
  }, []);

  useEffect(() => {
    load();
    api.get("/accounts").then((r) => setAccounts(r.data)).catch(() => {});
  }, [load]);

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({
      type: t.type, account_id: t.account_id, counter_account_id: t.counter_account_id || "",
      amount: String(t.amount), currency: t.currency, description: t.description || "", reference: t.reference || "",
    });
    setOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (payload.type !== "transfer") payload.counter_account_id = null;
      const acct = accounts.find((a) => a.id === payload.account_id);
      if (acct) payload.currency = acct.currency;
      if (editingId) {
        await api.put(`/transactions/${editingId}`, payload);
        toast.success("Transaction updated · ledger adjusted");
      } else {
        await api.post("/transactions", payload);
        toast.success("Transaction posted to ledger");
      }
      setOpen(false);
      setEditingId(null);
      setForm(EMPTY);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/transactions/${id}`);
      toast.success("Transaction reversed & deleted");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Transactions</h1>
          <p className="text-sm text-muted-foreground mt-1">Money movement — each entry auto-posts a double-entry ledger record</p>
        </div>
        {canWrite && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditingId(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="transaction-create-button" onClick={openCreate} className="bg-indigo-500 hover:bg-indigo-600 text-white">
                <Plus className="h-4 w-4 mr-2" /> New Transaction
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border text-foreground">
              <DialogHeader>
                <DialogTitle className="font-display">{editingId ? "Edit Transaction" : "New Transaction"}</DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  {editingId ? "Editing re-posts the double-entry ledger and adjusts balances." : "Posts a double-entry ledger record and updates balances."}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Type</Label>
                  <Select value={form.type} onValueChange={(v) => setF("type", v)}>
                    <SelectTrigger data-testid="txn-type" className="bg-muted/40 border-border"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border text-foreground">
                      <SelectItem value="inflow" className="focus:bg-accent focus:text-accent-foreground">Inflow (money in)</SelectItem>
                      <SelectItem value="outflow" className="focus:bg-accent focus:text-accent-foreground">Outflow (money out)</SelectItem>
                      <SelectItem value="transfer" className="focus:bg-accent focus:text-accent-foreground">Transfer (account → account)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">{form.type === "transfer" ? "From Account" : "Account"}</Label>
                  <Select value={form.account_id} onValueChange={(v) => setF("account_id", v)}>
                    <SelectTrigger data-testid="txn-account" className="bg-muted/40 border-border"><SelectValue placeholder="Select account" /></SelectTrigger>
                    <SelectContent className="bg-card border-border text-foreground">
                      {accounts.map((a) => (
                        <SelectItem key={a.id} value={a.id} className="focus:bg-accent focus:text-accent-foreground">
                          {a.name} · {a.currency} {fmtAmount(a.balance)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {form.type === "transfer" && (
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">To Account</Label>
                    <Select value={form.counter_account_id} onValueChange={(v) => setF("counter_account_id", v)}>
                      <SelectTrigger data-testid="txn-counter-account" className="bg-muted/40 border-border"><SelectValue placeholder="Select account" /></SelectTrigger>
                      <SelectContent className="bg-card border-border text-foreground">
                        {accounts.filter((a) => a.id !== form.account_id).map((a) => (
                          <SelectItem key={a.id} value={a.id} className="focus:bg-accent focus:text-accent-foreground">{a.name} · {a.currency}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Amount</Label>
                  <Input data-testid="txn-amount" type="number" value={form.amount} onChange={(e) => setF("amount", e.target.value)}
                    className="bg-muted/40 border-border font-mono" placeholder="0.00" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Description</Label>
                  <Input data-testid="txn-description" value={form.description} onChange={(e) => setF("description", e.target.value)}
                    className="bg-muted/40 border-border" placeholder="e.g. Card settlement" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)} className="text-muted-foreground">Cancel</Button>
                <Button data-testid="txn-save-button" onClick={submit} disabled={saving || !form.account_id || !form.amount}
                  className="bg-indigo-500 hover:bg-indigo-600 text-white">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingId ? "Save changes" : "Post Transaction"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                {["Type", "Reference", "Amount", "Currency", "Description", "Status", "Time"].map((h) => (
                  <TableHead key={h} className="text-muted-foreground text-xs uppercase tracking-wider">{h}</TableHead>
                ))}
                <TableHead className="text-right text-muted-foreground text-xs uppercase tracking-wider">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows === null && <TableRow><TableCell colSpan={8} className="text-center py-16"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></TableCell></TableRow>}
              {rows?.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-16 text-muted-foreground"><Inbox className="h-8 w-8 mx-auto mb-2 opacity-50" />No transactions yet</TableCell></TableRow>}
              {rows?.map((t) => {
                const meta = TYPE_META[t.type] || TYPE_META.transfer;
                const Icon = meta.icon;
                return (
                  <TableRow key={t.id} data-testid="transaction-row" className="border-border hover:bg-accent/60 transition-colors">
                    <TableCell><Badge className={`${meta.cls} capitalize gap-1`}><Icon className="h-3 w-3" />{t.type}</Badge></TableCell>
                    <TableCell className="font-mono text-xs text-foreground">{t.reference}</TableCell>
                    <TableCell className="font-mono">{fmtAmount(t.amount)}</TableCell>
                    <TableCell className="font-mono text-xs">{t.currency}</TableCell>
                    <TableCell className="text-foreground max-w-[220px] truncate">{t.description || "—"}</TableCell>
                    <TableCell><Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">{t.status}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(t.created_at)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="icon" onClick={() => setDetail(t)}
                          data-testid="transaction-view" className="text-muted-foreground hover:text-foreground hover:bg-muted mr-1">
                          <Eye className="h-4 w-4" />
                        </Button>
                        {canWrite && (<>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(t)}
                          data-testid="transaction-edit" className="text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10 mr-1">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => remove(t.id)}
                          data-testid="transaction-delete" className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        </>)}
                      </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
      <TransactionDetails txn={detail} accounts={accounts} onClose={() => setDetail(null)} />
    </div>
  );
}
